import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Trash2, Save, X, CalendarIcon, Loader2, FileText } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useProjectQuery, useUpdateProjectMutation, useDeleteProjectMutation, useGenerateReportMutation } from "@/hooks/queries/useProjectsQuery";
import { isSeatEntitlementError } from "@/lib/apiErrors";
import { useOrganizationQuery } from "@/hooks/queries/useOrganizationsQuery";
import { useWorkstreamsByProjectQuery } from "@/hooks/queries/useWorkstreamsQuery";
import { useProjectUsersQuery } from "@/hooks/queries/useProjectUsersQuery";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { DeleteProjectCommand, Project, UpdateProjectCommand } from "@/types/project";
import {
  ProjectStatus,
  ProjectPhase,
  RoleType,
  ReportGenerationStatus,
  Sector,
  SectorLabels,
  DealType,
} from "@/types/project";

// Tab components
import OverviewTab from "@/components/project/OverviewTab";
import WorkstreamsTab from "@/components/project/WorkstreamsTab";
// import DocumentsTab from "@/components/project/DocumentsTab";
import RisksTab from "@/components/project/RisksTab";
import TeamRolesTab from "@/components/project/TeamRolesTab";
import ProjectUsersTab from "@/components/project/ProjectUsersTab";
import FoldersTab from "@/components/project/FoldersTab";
import ActivityLog from "@/components/project/ActivityLog";

const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  // React Query hooks
  const { data: project, isLoading: loading, isError: projectError, error: projectQueryError } = useProjectQuery(Number(id));
  const { data: organization } = useOrganizationQuery(project?.organizationId ?? 0);
  const updateProjectMutation = useUpdateProjectMutation();
  const deleteProjectMutation = useDeleteProjectMutation();
  const generateReportMutation = useGenerateReportMutation();

  // Permission checks
  const { user, isLoading: authLoading } = useAuth();
  const currentUserId = Number(user?.id ?? 0);
  const { data: projectUsers = [], isLoading: projectUsersLoading } = useProjectUsersQuery(Number(id));
  const permissionsLoading = authLoading || projectUsersLoading;
  const currentMembership = projectUsers.find((pu) => pu.userId === currentUserId);
  const isOwner = currentMembership?.roleType === RoleType.Owner;
  const isEditor = currentMembership?.roleType === RoleType.Editor;
  const canEdit = isOwner || isEditor;
  const canDelete = isOwner;
  const isGenerating = project?.reportGenerationStatus === ReportGenerationStatus.Generating;
  // Keep the workstreams cache polling every minute while a report is generating, even when the
  // Workstreams tab isn't mounted (WorkstreamsTab reads the same shared query cache entry).
  useWorkstreamsByProjectQuery(project?.id ?? 0, { refetchInterval: isGenerating ? 60_000 : false });
  const canAffordReport = project && organization ? project.estimatedCreditCost <= organization.creditBalance : false;
  const canRunAgents = project?.status !== ProjectStatus.Done;
  const [reportGenerationElapsedSeconds, setReportGenerationElapsedSeconds] = useState<number | null>(null);

  const validTabs = ["overview", "workstreams", "risks", "team", "folders"];
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabParam && validTabs.includes(tabParam) ? tabParam : "overview"
  );
  const targetFolderId = searchParams.get("folderId") ? Number(searchParams.get("folderId")) : undefined;

  // Sync active tab when URL search params change (e.g. navigating between search results)
  useEffect(() => {
    const next = tabParam && validTabs.includes(tabParam) ? tabParam : "overview";
    setActiveTab(next);
  }, [tabParam]);
  const [editing, setEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGenerateReportDialog, setShowGenerateReportDialog] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    targetName: "",
    status: "",
    phase: "",
    expectedClose: undefined as Date | undefined,
    sector: "" as string,
    dealType: "" as string,
    redacted: "",
    owner: "",
  });

  // Update edit form when project data loads
  useEffect(() => {
    if (project) {
      populateEditForm(project);
    }
  }, [project]);

  useEffect(() => {
    if (!isGenerating || !project?.reportGenerationStatusChangedAt) {
      setReportGenerationElapsedSeconds(null);
      return;
    }

    const startedAt = Date.parse(project.reportGenerationStatusChangedAt);
    if (Number.isNaN(startedAt)) {
      setReportGenerationElapsedSeconds(null);
      return;
    }

    const updateElapsedTime = () => {
      setReportGenerationElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    };

    updateElapsedTime();
    const intervalId = window.setInterval(updateElapsedTime, 1000);
    return () => window.clearInterval(intervalId);
  }, [isGenerating, project?.reportGenerationStatusChangedAt]);

  const getGeneratingLabel = () => {
    if (reportGenerationElapsedSeconds === null) {
      return t("generatingReport");
    }

    const elapsedHours = Math.floor(reportGenerationElapsedSeconds / 3600);
    const elapsedMinutes = Math.floor((reportGenerationElapsedSeconds % 3600) / 60);
    const elapsedSeconds = reportGenerationElapsedSeconds % 60;
    const elapsedParts: string[] = [];

    if (elapsedHours > 0) {
      elapsedParts.push(`${elapsedHours}h`);
    }
    if (elapsedMinutes > 0) {
      elapsedParts.push(`${elapsedMinutes}m`);
    }
    elapsedParts.push(`${elapsedSeconds}s`);

    return `${t("generatingReport")} (${elapsedParts.join(" ")})`;
  };

  const populateEditForm = (p: Project) => {
    setEditForm({
      name: p.name || "",
      targetName: p.targetName || "",
      status: p.status !== null && p.status !== undefined ? String(p.status) : "",
      phase: p.phase !== null && p.phase !== undefined ? String(p.phase) : "",
      expectedClose: p.expectedClose ? new Date(p.expectedClose.substring(0, 10) + "T12:00:00") : undefined,
      sector: p.sector !== null && p.sector !== undefined ? String(p.sector) : "",
      dealType: p.dealType !== null && p.dealType !== undefined ? String(p.dealType) : "",
      redacted: p.redacted || "",
      owner: p.owner || "",
    });
  };

  const handleStartEdit = () => {
    if (project) {
      populateEditForm(project);
      setEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    if (project) populateEditForm(project);
  };

  const handleSave = async () => {
    if (!project) return;
    const parsedStatus =
      editForm.status !== "" ? (Number(editForm.status) as ProjectStatus) : project.status;
    const parsedPhase =
      editForm.phase !== "" ? (Number(editForm.phase) as ProjectPhase) : project.phase;

    const command: UpdateProjectCommand = {
      id: project.id,
      name: editForm.name,
      targetName: editForm.targetName || editForm.name,
      status: parsedStatus,
      phase: parsedPhase,
      expectedClose: editForm.expectedClose ? format(editForm.expectedClose, "yyyy-MM-dd") : null,
      sector: editForm.sector !== "" ? Number(editForm.sector) as Sector : null,
      dealType: editForm.dealType !== "" ? Number(editForm.dealType) as DealType : null,
      redacted: editForm.redacted,
      owner: editForm.owner || null,
      rowVersion: project.rowVersion,
      updatedBy: "current-user",
    };

    try {
      await updateProjectMutation.mutateAsync({ id: project.id, command });
      setEditing(false);
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  const handleDelete = async () => {
    if (!project) return;
    try {
      const command: DeleteProjectCommand = { id: project.id, rowVersion: project.rowVersion };
      await deleteProjectMutation.mutateAsync({ id: project.id, command });
      navigate("/project");
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error(error instanceof Error ? error.message : t("deleteHasCreditsError"));
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const getPhaseLabel = (phase: ProjectPhase | null) => {
    if (phase === null || phase === undefined) return "-";
    switch (phase) {
      case ProjectPhase.InitialReview: return t("initialReview");
      case ProjectPhase.DueDiligence: return t("dueDiligence");
      case ProjectPhase.Negotiation: return "Negotiation";
      case ProjectPhase.Closing: return t("closing");
      default: return "-";
    }
  };

  const getStatusColor = (status: ProjectStatus | null) => {
    switch (status) {
      case ProjectStatus.Active: return "bg-green-500";
      case ProjectStatus.OnHold: return "bg-yellow-500";
      case ProjectStatus.Cancelled: return "bg-red-500";
      case ProjectStatus.Done: return "bg-blue-500";
      default: return "bg-gray-400";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-muted-foreground" size={32} />
        </div>
      </DashboardLayout>
    );
  }

  // Check this before the stale-cache fallback below: React Query keeps serving the
  // previously cached project data while a background refetch is in flight/failing, so
  // `project` can still be truthy even after the user loses access (e.g. seat entitlement
  // revoked). Without this check, the cached detail page would remain visible instead of
  // showing the access-blocked message.
  if (projectError && isSeatEntitlementError(projectQueryError)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">{t("seatEntitlementBlockedMessage")}</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">{t("projectNotFound")}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/project")}>
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(project.status)}`} />
              <div>
                <h1 className="text-2xl font-bold text-foreground">{project.name}</h1>
                <p className="text-muted-foreground">
                  {project.targetName && project.targetName !== project.name ? `${project.targetName} • ` : ""}
                  {getPhaseLabel(project.phase)} • {project.owner || "-"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("organization")}:{" "}
                  <Link className="text-primary hover:underline" to={`/organization/${project.organizationId}`}>
                    {organization?.name ?? project.organizationId}
                  </Link>
                </p>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {!editing ? (
              <>
                {project.reportGenerationStatus === ReportGenerationStatus.Generating ? (
                  <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto" disabled>
                    <Loader2 className="animate-spin" size={16} />
                    {getGeneratingLabel()}
                  </Button>
                ) : (
                  (canEdit || permissionsLoading) && canRunAgents && (
                    <Button
                      size="sm"
                      className="w-full gap-1 sm:w-auto"
                      onClick={() => setShowGenerateReportDialog(true)}
                      disabled={generateReportMutation.isPending || !canAffordReport || permissionsLoading}
                    >
                      {generateReportMutation.isPending ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <FileText size={16} />
                      )}
                      {t("generateReport")}
                    </Button>
                  )
                )}
                {(canEdit || permissionsLoading) && !isGenerating && (
                  <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto" onClick={handleStartEdit} disabled={permissionsLoading}>
                    <Pencil size={16} />
                    {t("edit")}
                  </Button>
                )}
                {(canDelete || permissionsLoading) && !isGenerating && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1 text-destructive hover:text-destructive sm:w-auto"
                    onClick={handleDeleteClick}
                    disabled={permissionsLoading}
                  >
                    <Trash2 size={16} />
                    {t("delete")}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button size="sm" className="w-full gap-1 sm:w-auto" onClick={handleSave} disabled={updateProjectMutation.isPending}>
                  {updateProjectMutation.isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  {t("save")}
                </Button>
                <Button variant="outline" size="sm" className="w-full gap-1 sm:w-auto" onClick={handleCancelEdit} disabled={updateProjectMutation.isPending}>
                  <X size={16} />
                  {t("cancel")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Edit Form */}
        {editing && (
          <div className="bg-card p-6 rounded-lg border border-border space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("projectTargetName")} *</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t("projectTarget")}</Label>
                <Input
                  value={editForm.targetName}
                  onChange={(e) => setEditForm({ ...editForm, targetName: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("status")}</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue placeholder={t("selectStatus")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(ProjectStatus.Active)}>{t("active")}</SelectItem>
                    <SelectItem value={String(ProjectStatus.OnHold)}>{t("onHold")}</SelectItem>
                    <SelectItem value={String(ProjectStatus.Cancelled)}>{t("cancelled")}</SelectItem>
                    <SelectItem value={String(ProjectStatus.Done)}>{t("done")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("phase")}</Label>
                <Select value={editForm.phase} onValueChange={(v) => setEditForm({ ...editForm, phase: v })}>
                  <SelectTrigger><SelectValue placeholder={t("selectPhase")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(ProjectPhase.InitialReview)}>{t("initialReview")}</SelectItem>
                    <SelectItem value={String(ProjectPhase.DueDiligence)}>{t("dueDiligence")}</SelectItem>
                    <SelectItem value={String(ProjectPhase.Negotiation)}>{t("negotiation")}</SelectItem>
                    <SelectItem value={String(ProjectPhase.Closing)}>{t("closing")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>{t("expectedCloseDate")}</Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !editForm.expectedClose && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.expectedClose ? format(editForm.expectedClose, "PPP") : t("expectedCloseDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={editForm.expectedClose}
                      onSelect={(date) => {
                        setEditForm({ ...editForm, expectedClose: date });
                        setDatePickerOpen(false);
                      }}
                      autoFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("owner")}</Label>
              <Input
                value={editForm.owner}
                onChange={(e) => setEditForm({ ...editForm, owner: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("projectSector")}</Label>
                <Select value={editForm.sector} onValueChange={(v) => setEditForm({ ...editForm, sector: v })}>
                  <SelectTrigger><SelectValue placeholder={t("selectSector")} /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SectorLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("projectDealType")}</Label>
                <Select value={editForm.dealType} onValueChange={(v) => setEditForm({ ...editForm, dealType: v })}>
                  <SelectTrigger><SelectValue placeholder={t("selectDealType")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(DealType.Acquisition)}>{t("dealTypeAcquisition")}</SelectItem>
                    <SelectItem value={String(DealType.Merger)}>{t("dealTypeMerger")}</SelectItem>
                    <SelectItem value={String(DealType.GrowthInvestment)}>{t("dealTypeGrowthInvestment")}</SelectItem>
                    <SelectItem value={String(DealType.PEBuyout)}>{t("dealTypePEBuyout")}</SelectItem>
                    <SelectItem value={String(DealType.CarveOut)}>{t("dealTypeCarveOut")}</SelectItem>
                    <SelectItem value={String(DealType.JointVenture)}>{t("dealTypeJointVenture")}</SelectItem>
                    <SelectItem value={String(DealType.AssetPurchase)}>{t("dealTypeAssetPurchase")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("redacted")} *</Label>
              <Textarea
                value={editForm.redacted}
                onChange={(e) => setEditForm({ ...editForm, redacted: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">{t("redactedCommaSeparated")}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        {!editing && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto gap-1 bg-transparent p-0 border-b rounded-none">
            <TabsTrigger
              value="overview"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none"
            >
              {t("tabOverview")}
            </TabsTrigger>

            <TabsTrigger
              value="team"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none"
            >
              {t("tabTeamRoles")}
            </TabsTrigger>

            <TabsTrigger
              value="folders"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none"
            >
              {t("tabFolders")}
            </TabsTrigger>

            <TabsTrigger
              value="workstreams"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none"
            >
              {t("tabWorkstreams")}
            </TabsTrigger>

            {/* <TabsTrigger
              value="documents"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none"
            >
              {t("tabDocuments")}
            </TabsTrigger> */}
            <TabsTrigger
              value="risks"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none data-[state=active]:shadow-none"
            >
              {t("tabRisks")}
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="overview" className="m-0">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3">
                  <OverviewTab project={project} />
                </div>
                <div className="lg:col-span-1">
                  <ActivityLog projectId={project.id} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="team" className="m-0">
              <ProjectUsersTab projectId={project.id} />
            </TabsContent>

            <TabsContent value="folders" className="m-0">
              <FoldersTab projectId={project.id} targetFolderId={targetFolderId} readOnly={project.reportGenerationStatus === ReportGenerationStatus.Generating} />
            </TabsContent>

            <TabsContent value="workstreams" className="m-0">
              <WorkstreamsTab projectId={project.id} />
            </TabsContent>

            {/* <TabsContent value="documents" className="m-0">
              <DocumentsTab projectId={String(project.id)} />
            </TabsContent> */}

            <TabsContent value="risks" className="m-0">
              <RisksTab projectId={String(project.id)} />
            </TabsContent>

          </div>
        </Tabs>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteProject")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmDeleteProjectDesc")} <strong>{project.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteProjectMutation.isPending}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteProjectMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteProjectMutation.isPending ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Run AI Workflows confirmation dialog */}
      {canRunAgents && (
        <AlertDialog open={showGenerateReportDialog} onOpenChange={setShowGenerateReportDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("confirmGenerateReport")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("confirmGenerateReportDesc")}
                <div className="mt-3 space-y-1 text-sm">
                  <p>{t("organizationCreditBalance")}: {organization ? organization.creditBalance.toFixed(2) : "-"}</p>
                  <p>{t("requiredCredits")}: {project.estimatedCreditCost.toFixed(2)}</p>
                  {!canAffordReport ? (
                    <p className="text-destructive font-medium">{t("insufficientProjectCredits")}</p>
                  ) : null}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={generateReportMutation.isPending}>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  generateReportMutation.mutate(project.id, {
                    onSuccess: () => setShowGenerateReportDialog(false),
                  });
                }}
                disabled={generateReportMutation.isPending || !canAffordReport}
              >
                {generateReportMutation.isPending ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {t("generateReport")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </DashboardLayout>
  );
};

export default ProjectDetailPage;
